import { Banknote, Landmark } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum, round2, vals } from "@/lib/format";
import { FX_BANKS, BankLogo } from "@/components/exchange/Transfers";

const onDay = (iso, d) => (iso || "").slice(0, 10) === d;
const given = (x) => round2(x.kind === "buy" ? x.total - (x.fee || 0) : x.total + (x.fee || 0));

export function fxDayRegisters(view, d) {
  const all = vals(view.fx_trades).filter((x) => !x.voided).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const reg = (k) => ({ k, n: 0, fx: {}, dzd_out: 0, dzd_in: 0, ops: [] });
  const banks = Object.fromEntries(FX_BANKS.map((b) => [b.k, reg(b.k)]));
  const cash = reg("cash");
  const fondo = { open: 0, in: 0, out: 0, adj_in: 0, adj_out: 0, pending: 0 };
  for (const x of all) {
    const day = onDay(x.created_at, d);
    const before = (x.created_at || "").slice(0, 10) < d;
    if (x.type === "adjust") {
      if (x.currency !== "DZD") continue;
      const v = x.kind === "in" ? x.amount : -x.amount;
      if (before) fondo.open += v; else if (day) { if (x.kind === "in") fondo.adj_in += x.amount; else fondo.adj_out += x.amount; }
      continue;
    }
    const g = given(x);
    const settled = x.status !== "pending";
    const payDay = x.status === "paid" ? (x.paid_at || x.created_at) : x.created_at;
    if (x.kind === "buy") { if (settled) { if ((payDay || "").slice(0, 10) < d) fondo.open -= g; else if (onDay(payDay, d)) fondo.out += g; } else if (day) fondo.pending += g; }
    else { if (before) fondo.open += g; else if (day) fondo.in += g; }
    if (!day) continue;
    const r = x.method === "transfer" ? banks[x.bank] || cash : cash;
    r.n++; r.fx[x.currency] = (r.fx[x.currency] || 0) + (x.kind === "buy" ? x.amount : -x.amount);
    if (x.kind === "buy") r.dzd_out = round2(r.dzd_out + g); else r.dzd_in = round2(r.dzd_in + g);
    r.ops.push(x);
  }
  fondo.open = round2(fondo.open); fondo.close = round2(fondo.open + fondo.in - fondo.out + fondo.adj_in - fondo.adj_out);
  return { banks, cash, fondo, date: d };
}

function RegCard({ r, logo, title, t, money, lang, color }) {
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden" style={{ borderTopColor: color, borderTopWidth: 3 }} data-testid={`fx-reg-${r.k}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-[#F7F7F7] border-b border-[#E5E5E5]"><span className="flex items-center gap-2 text-sm font-bold">{logo}{title}</span><span className="text-xs text-muted-foreground">{r.n} {t("fx_ledger").toLowerCase()}</span></div>
      <div className="grid grid-cols-3 text-center text-xs p-2 gap-1">
        <div><div className="text-muted-foreground">{t("fx_in")}</div><div className="font-black" dir="ltr">{Object.entries(r.fx).filter(([, v]) => v > 0).map(([c, v]) => `${fmtNum(v)} ${c}`).join(" · ") || "—"}</div></div>
        <div><div className="text-muted-foreground">DZD {t("fx_given")}</div><div className="font-black text-[#C7353B]" dir="ltr">{money(r.dzd_out)}</div></div>
        <div><div className="text-muted-foreground">DZD {t("fx_received")}</div><div className="font-black text-[#1F9D55]" dir="ltr">{money(r.dzd_in)}</div></div>
      </div>
      {!!r.ops.length && <ul className="border-t border-[#E5E5E5] max-h-40 overflow-auto divide-y divide-[#F0F0F0] text-[12px]" data-testid={`fx-reg-ops-${r.k}`}>
        {r.ops.map((x) => <li key={x.id} className="px-3 py-1 flex items-center justify-between gap-2"><span className="truncate"><b dir="ltr">{x.receipt_no}</b> · {fmtDate(x.created_at, lang)} · {x.receiver_name ? `${x.sender_name} → ${x.receiver_name}` : x.customer_name || x.staff_name}</span><span className="whitespace-nowrap" dir="ltr">{x.kind === "buy" ? "+" : "−"}{fmtNum(x.amount)} {x.currency} @ {fmtNum(x.rate)} = <b className={x.kind === "buy" ? "text-[#C7353B]" : "text-[#1F9D55]"}>{x.kind === "buy" ? "−" : "+"}{money(given(x))}</b>{x.status === "pending" ? ` · ${t("fx_pending")}` : ""}</span></li>)}
      </ul>}
    </div>
  );
}

export function FxRegisters({ date, compact }) {
  const { t, view, money, lang } = useApp();
  const d = date || new Date().toISOString().slice(0, 10);
  const { banks, cash, fondo } = fxDayRegisters(view, d);
  return (
    <div className="space-y-3" data-testid="fx-registers">
      <div className="rounded-xl border-2 border-[#1F9D55] bg-[#F3FAF5] p-3" data-testid="fx-fondo">
        <div className="flex items-center justify-between mb-2"><span className="flex items-center gap-2 font-bold"><Banknote className="w-5 h-5 text-[#1F9D55]" />{t("fx_fondo_dzd")} · <span dir="ltr">{d}</span></span><span className="text-xs text-muted-foreground">{t("fx_fondo_hint")}</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-center text-xs">
          {[["fx_fondo_open", fondo.open, "#1F4E8C"], ["fx_float_in", fondo.adj_in, "#1F4E8C"], ["fx_received", fondo.in, "#1F9D55"], ["fx_given", fondo.out, "#C7353B"], ["fx_float_out", fondo.adj_out, "#C7353B"], ["fx_fondo_close", fondo.close, fondo.close < 0 ? "#C7353B" : "#1F9D55"]].map(([k, v, c]) => (
            <div key={k} className="rounded-lg bg-white border border-border p-2" data-testid={`fx-fondo-${k}`}><div className="text-muted-foreground">{t(k)}</div><div className="font-black text-base" dir="ltr" style={{ color: c }}>{money(v)}</div></div>))}
        </div>
        {fondo.pending > 0 && <div className="mt-2 text-xs font-bold text-[#A85A12]" data-testid="fx-fondo-pending">{t("fx_pending_pickups")}: {money(fondo.pending)}</div>}
      </div>
      <div className={`grid gap-2 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 xl:grid-cols-5"}`}>
        <RegCard r={cash} logo={<Banknote className="w-4 h-4" />} title={t("fin_reg_tpv")} t={t} money={money} lang={lang} color="#1F9D55" />
        {FX_BANKS.map((b) => <RegCard key={b.k} r={banks[b.k]} logo={<BankLogo k={b.k} className="h-4" />} title="" t={t} money={money} lang={lang} color={b.color} />)}
      </div>
      {!compact && <p className="text-xs text-muted-foreground flex items-center gap-1"><Landmark className="w-3.5 h-3.5" />{t("fx_registers_hint")}</p>}
    </div>
  );
}
